import React from "react"
import { cn } from "@kit/design/utils"
import { VisuallyHidden } from "@kit/design/visually-hidden"
import { useHasUnseenSponsorshipOffers } from "hooks/use-has-unseen-sponsorship-offers"

/**
 * Dot marking unseen sponsorship offers in the vertical nav. Rendered in two
 * places so the cue follows the accordion:
 *
 * - On the Sponsorships item's `trailing` (default) — visible only while the
 *   Monetization group is open, since the item lives in the collapsible panel.
 * - On the Monetization group's `trailing` with `hideWhenGroupExpanded` — the
 *   group trigger carries Tailwind's `group` + `aria-expanded`, so the dot is
 *   hidden while open and shows only when the group is collapsed.
 *
 * Renders nothing when there's nothing new, so it's safe to always pass as
 * `trailing`. The dot is the only cue for new offers, so it carries an sr-only
 * label rather than being purely decorative.
 */
export function SponsorshipNavDot({ hideWhenGroupExpanded = false }) {
  const hasUnseenOffers = useHasUnseenSponsorshipOffers()

  if (!hasUnseenOffers) return null

  return (
    <span
      className={cn(
        "flex items-center",
        hideWhenGroupExpanded && "group-aria-expanded:hidden"
      )}
    >
      <span aria-hidden="true" className="size-2 rounded-full bg-blue-500" />
      <VisuallyHidden>New sponsorship offers</VisuallyHidden>
    </span>
  )
}
